import {
    baselineValueForField,
    calculatePricingValueChangeRatio,
    pricingFieldValuesEqual,
} from '@/utils/priceListPricingFieldBaselines';
import {
    DEFAULT_PRICING_SETTINGS,
    builderFormToCalculatorInput,
    calculatePriceAdjustmentRatio,
    calculateProductPricing,
    getDefaultFurnizorPricing,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
    percentageInputToRatio,
    roundMoney,
    setDefaultFurnizorPricing,
} from '@/utils/priceListProductCalculator';
import { builderFormToPricingDetailPayload } from '@/utils/pricingDetailMapper';
import { TRANSPORT_COST_MODE_FORMULA } from '@/utils/transportCostBreakdown';
import { cloneDeep } from 'lodash';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * Draft-table "preț vechi" source for Preț intrare RON cu TVA:
 * open-session baselines frozen at the published/lista-veche product
 * (not last-în-lucru / Valoare veche, which advances on reopen+save).
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveEntryPriceRonIncVatOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('entryPriceRonIncVat', session),
    );
}

/**
 * Draft-table "preț vechi" source for Preț intrare RON fără TVA.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveEntryPriceRonExVatOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('entryPriceRonExVat', session),
    );
}

/**
 * Draft-table previous sale/list price from open-session baselines
 * (lista veche), not last-în-lucru Valoare veche baselines.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveListPricePreviousFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('listPriceRonIncVatFinal', session),
    );
}

/**
 * Draft-table "preț listă vechi furnizor" from open-session baselines
 * (lista veche). Form.supplierListPriceOld is often normalized equal to new
 * after draft save; last-în-lucru baselines must not be used here.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveSupplierListPriceOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return toNullableNumber(
        baselineValueForField('supplierListPriceNew', session),
    );
}

/**
 * Form percent ("40" / "40,00") → ratio (0.4). Blank is missing, not 0%.
 *
 * @param {unknown} value
 * @returns {number|null}
 */
function ratioFromMarkupPercentInput(value) {
    if (value === null || value === undefined || String(value).trim() === '') {
        return null;
    }
    const ratio = percentageInputToRatio(value);
    return Number.isFinite(ratio) ? ratio : null;
}

/**
 * Draft-table "adaos vechi": open-session adaos actual frozen at lista veche.
 * Baselines store the form percent, not the API ratio.
 *
 * @param {Object|null|undefined} builderState
 * @param {string|null|undefined} tabKey
 * @returns {number|null}
 */
export function resolveMarkupActualOldFromBaselines(builderState, tabKey) {
    if (!builderState || !tabKey) {
        return null;
    }
    const session = builderState.pricingFieldBaselinesByTab?.[tabKey];
    return ratioFromMarkupPercentInput(
        baselineValueForField('markupActual', session),
    );
}

/**
 * Point difference of adaos actual (new − old), as a ratio for the table.
 * 85% → 75% is −0.10 and displays as −10%, not the relative −11,76%.
 *
 * @param {number|null|undefined} currentRatio
 * @param {number|null|undefined} oldRatio
 * @returns {number|null}
 */
function markupActualChangeRatio(currentRatio, oldRatio) {
    const current = toNullableNumber(currentRatio);
    const old = toNullableNumber(oldRatio);
    if (current == null || old == null) {
        return null;
    }
    return roundMoney(current - old, 6);
}

/**
 * @param {Object|null|undefined} furnizorTab
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} builderState
 * @param {Object|null|undefined} liveItem
 * @returns {{
 *   supplier_list_price_old: number|null,
 *   supplier_list_price_new: number|null,
 *   supplier_list_price_adjustment_ratio: number|null,
 *   currency: string,
 *   is_default: boolean,
 * }}
 */
function resolveSupplierListDisplayFields(
    furnizorTab = null,
    form = null,
    builderState = null,
    liveItem = null,
) {
    const tab = furnizorTab
        || (form ? getDefaultFurnizorPricing(form) : null)
        || form?.furnizorPricings?.[0]
        || null;
    const liveDetail = liveItem?.pricing_detail || null;
    const liveTabs = Array.isArray(liveDetail?.furnizorPricings)
        ? liveDetail.furnizorPricings
        : [];
    const liveTab = tab
        ? findMatchingLiveFurnizorTab(tab, liveTabs)
        : (liveTabs.find((row) => row?.isDefault) || liveTabs[0] || null);
    const source = tab || liveTab || liveDetail || null;

    const tabKey = String(tab?.clientKey || liveTab?.clientKey || '').trim();
    const supplierNew = toNullableNumber(
        source?.supplierListPriceNew ?? liveDetail?.supplierListPriceNew,
    );
    // Lista veche first: published live tab, then frozen open baselines.
    // Do not use draft form old (normalized after save) or last-în-lucru.
    const supplierOld = toNullableNumber(
        liveTab?.supplierListPriceNew ?? liveTab?.supplierListPriceOld,
    )
        ?? resolveSupplierListPriceOldFromBaselines(builderState, tabKey)
        ?? toNullableNumber(
            liveDetail?.supplierListPriceNew ?? liveDetail?.supplierListPriceOld,
        )
        ?? toNullableNumber(source?.supplierListPriceOld)
        ?? supplierNew;

    const currencyRaw = String(
        source?.currency
            ?? form?.currency
            ?? liveDetail?.currency
            ?? 'ron',
    ).trim().toLowerCase() || 'ron';

    const isDefault = tab
        ? Boolean(tab.isDefault)
        : liveTab
            ? Boolean(liveTab.isDefault)
            : true;

    return {
        supplier_list_price_old: supplierOld,
        supplier_list_price_new: supplierNew,
        supplier_list_price_adjustment_ratio: calculatePricingValueChangeRatio(
            supplierNew,
            supplierOld,
        ),
        currency: currencyRaw,
        is_default: isDefault,
    };
}

/**
 * Fields compared when deciding if a furnizor tab was modified vs live.
 * Keep aligned with `furnizorPricingFormToPayload` — do not include form-only
 * fields that are omitted on save (those caused false „Actualizare” rows).
 *
 * Intentionally omit derived outputs rewritten on open/save for every tab:
 * - `markupActual` (normalizeDerivedMarkupActualOnAllFurnizorTabs)
 * - formula-auto `transportCostRate` (green calculator; compared separately and skipped)
 * @type {readonly string[]}
 */
const FURNIZOR_TAB_COMPARE_FIELDS = Object.freeze([
    'supplierListPriceNew',
    'currency',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
    'overrideListPriceRonIncVatFinalValue',
    'furnizorId',
    'furnizorCode',
    'furnizorName',
    'usesManufacturerAsFurnizor',
    'isDefault',
]);

/** Inputs that move entry or sale price. Not list membership / identity. */
const FURNIZOR_TAB_PRICE_FIELDS = Object.freeze([
    'supplierListPriceNew',
    'currency',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
    'overrideListPriceRonIncVatFinalValue',
]);

/** Furnizor identity. A price-only edit must not count as this. */
const FURNIZOR_TAB_STRUCTURE_FIELDS = Object.freeze([
    'furnizorId',
    'furnizorCode',
    'furnizorName',
    'usesManufacturerAsFurnizor',
    'isDefault',
]);

/** Ratio fields stored as decimals; form round-trips via 2-decimal %. */
const FURNIZOR_TAB_RATIO_COMPARE_FIELDS = new Set([
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
]);

/**
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function isFormulaAutoTransportRate(tab) {
    const breakdown = tab?.transportCostBreakdown;
    if (!breakdown || typeof breakdown !== 'object') {
        return false;
    }
    return breakdown.mode === TRANSPORT_COST_MODE_FORMULA
        && !breakdown.rateIsManual;
}

/**
 * Ratio equality that tolerates null/''/0 and 2-decimal % round-trip noise
 * (e.g. live 0.023456 vs payload 0.0235 after formula / form save).
 *
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
function areComparableRatesEqual(left, right) {
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty && rightEmpty) {
        return true;
    }
    const leftNum = leftEmpty ? 0 : parseNumericInput(left);
    const rightNum = rightEmpty ? 0 : parseNumericInput(right);
    if (!Number.isFinite(leftNum) || !Number.isFinite(rightNum)) {
        return normalizeComparableNumber(left) === normalizeComparableNumber(right);
    }
    const abs = Math.abs(leftNum - rightNum);
    // ~0.005 percentage points after ratio↔%↔ratio (2 decimals on the form).
    return abs <= 5e-5;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparableScalar(value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }
    return String(value).trim().toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? String(num) : normalizeComparableScalar(value);
}

/**
 * Live furnizor tabs for draft change detection. Falls back to a single
 * synthetic tab from flat pricing_detail / item columns (legacy products).
 *
 * @param {Object|null|undefined} liveItem
 * @returns {Object[]}
 */
export function resolveLiveFurnizorTabsForDraftCompare(liveItem) {
    if (!liveItem || typeof liveItem !== 'object') {
        return [];
    }
    const detail = liveItem.pricing_detail;
    const tabs = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    if (tabs.length) {
        return tabs;
    }
    if (!detail || typeof detail !== 'object') {
        const furnizorLabel = String(liveItem.furnizor || '').trim();
        if (
            liveItem.supplier_list_price == null
            && liveItem.price == null
            && !furnizorLabel
        ) {
            return [];
        }
        return [{
            isDefault: true,
            furnizorName: furnizorLabel,
            supplierListPriceNew: liveItem.supplier_list_price,
            currency: liveItem.currency,
            markupReference: liveItem.markup_reference,
            markupActual: liveItem.markup_actual,
            listPriceRonIncVatFinal: liveItem.price,
            entryPriceRonExVat: liveItem.entry_price_ron_ex_vat,
        }];
    }
    return [{
        ...detail,
        isDefault: true,
        furnizorId: detail.furnizorId ?? null,
        furnizorCode: detail.furnizorCode || '',
        furnizorName: String(
            detail.furnizorName
                || liveItem.furnizor
                || '',
        ).trim(),
        supplierListPriceNew:
            detail.supplierListPriceNew
            ?? liveItem.supplier_list_price,
        currency: detail.currency ?? liveItem.currency,
        markupReference: detail.markupReference ?? liveItem.markup_reference,
        markupActual: detail.markupActual ?? liveItem.markup_actual,
        listPriceRonIncVatFinal:
            detail.listPriceRonIncVatFinal
            ?? liveItem.price,
    }];
}

/**
 * @param {Object|null|undefined} tab
 * @param {Object[]|null|undefined} liveTabs
 * @returns {Object|null}
 */
export function findMatchingLiveFurnizorTab(tab, liveTabs = []) {
    const rows = Array.isArray(liveTabs) ? liveTabs : [];
    if (!tab || !rows.length) {
        return null;
    }
    if (tab.id != null && tab.id !== '') {
        const byId = rows.find((row) => row?.id != null && String(row.id) === String(tab.id));
        if (byId) {
            return byId;
        }
    }
    if (tab.furnizorId != null && tab.furnizorId !== '') {
        const byFurnizorId = rows.find(
            (row) => row?.furnizorId != null && String(row.furnizorId) === String(tab.furnizorId),
        );
        if (byFurnizorId) {
            return byFurnizorId;
        }
    }
    const code = String(tab.furnizorCode || '').trim().toLowerCase();
    if (code) {
        const byCode = rows.find(
            (row) => String(row?.furnizorCode || '').trim().toLowerCase() === code,
        );
        if (byCode) {
            return byCode;
        }
    }
    const clientKey = String(tab.clientKey || '').trim();
    if (clientKey) {
        const byKey = rows.find(
            (row) => String(row?.clientKey || '').trim() === clientKey,
        );
        if (byKey) {
            return byKey;
        }
    }
    const name = String(tab.furnizorName || '').trim().toLowerCase();
    if (name) {
        const byName = rows.find(
            (row) => String(row?.furnizorName || '').trim().toLowerCase() === name,
        );
        if (byName) {
            return byName;
        }
    }
    // Import often stores „producător ca furnizor” with empty furnizorName/code/id.
    // UI still shows manufacturerName as „Hirsch”, but identity fields are blank —
    // match on the flag (+ default) so adding Poliplast does not relabel Hirsch
    // as „Furnizor nou”.
    if (tab.usesManufacturerAsFurnizor) {
        const mfrRows = rows.filter((row) => Boolean(row?.usesManufacturerAsFurnizor));
        if (mfrRows.length === 1) {
            return mfrRows[0];
        }
        if (mfrRows.length > 1) {
            const byDefault = mfrRows.find(
                (row) => Boolean(row?.isDefault) === Boolean(tab.isDefault),
            );
            if (byDefault) {
                return byDefault;
            }
        }
    }
    return null;
}

/**
 * @param {Object|null|undefined} draftTab
 * @param {Object|null|undefined} liveTab
 * @returns {boolean}
 */
/**
 * Form-shaped tab vs open-session baseline (also form %). Used when the live
 * product is missing from the paginated table after remount — without this,
 * every existing supplier would be treated as Actualizare.
 *
 * @param {Object|null|undefined} formTab
 * @param {Object|null|undefined} baseline
 * @returns {boolean}
 */
export function isFurnizorTabModifiedVsBaseline(formTab, baseline) {
    if (!formTab || !baseline) {
        return Boolean(formTab);
    }

    if (Object.prototype.hasOwnProperty.call(formTab, 'discounts')) {
        const formDiscounts = Array.isArray(formTab.discounts) ? formTab.discounts : [];
        const baseDiscounts = Array.isArray(baseline.discounts) ? baseline.discounts : [];
        if (formDiscounts.length !== baseDiscounts.length) {
            return true;
        }
        for (let i = 0; i < formDiscounts.length; i += 1) {
            if (!pricingFieldValuesEqual(
                `discounts.${i}`,
                formDiscounts[i],
                baseDiscounts[i],
            )) {
                return true;
            }
        }
    }

    const fields = [
        'supplierListPriceNew',
        'currency',
        'discount1',
        'discount2',
        'cashDiscount',
        'marketingDiscount',
        'transportCostRate',
        'markupReference',
        'overrideListPriceRonIncVatFinalValue',
    ];
    return fields.some((field) => {
        if (!Object.prototype.hasOwnProperty.call(formTab, field)) {
            return false;
        }
        if (
            field === 'transportCostRate'
            && (
                isFormulaAutoTransportRate(formTab)
                || isFormulaAutoTransportRate(baseline)
            )
        ) {
            return false;
        }
        if (field === 'overrideListPriceRonIncVatFinalValue') {
            if (
                !formTab.overrideListPriceRonIncVatFinal
                && !baseline.overrideListPriceRonIncVatFinal
            ) {
                return false;
            }
        }
        if (field === 'currency') {
            return (
                normalizeComparableScalar(formTab[field])
                !== normalizeComparableScalar(baseline[field])
            );
        }
        if (!Object.prototype.hasOwnProperty.call(baseline, field)) {
            return false;
        }
        return !pricingFieldValuesEqual(field, formTab[field], baseline[field]);
    });
}

/**
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} tab form or payload tab
 * @param {Object[]|null|undefined} formTabs
 * @returns {Object|null} open-session baseline for the tab, if any
 */
function resolveOpenBaselineForDraftTab(draft, tab, formTabs = []) {
    const candidates = [
        tab,
        findMatchingLiveFurnizorTab(tab, formTabs),
    ].filter(Boolean);
    const baselines = draft?.builderState?.pricingFieldBaselinesByTab;
    if (!baselines || typeof baselines !== 'object') {
        return null;
    }
    for (const candidate of candidates) {
        const key = resolveDraftFurnizorTabKey(candidate);
        if (key && baselines[key]) {
            return baselines[key];
        }
        const clientKey = String(candidate?.clientKey || '').trim();
        if (clientKey && baselines[clientKey]) {
            return baselines[clientKey];
        }
    }
    return null;
}

/**
 * When live product is absent (not on current page after remount), decide if a
 * furnizor tab should still show as Actualizare / Furnizor nou.
 *
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} formOrPayloadTab
 * @param {Object|null|undefined} payloadTab
 * @param {Object[]|null|undefined} formTabs
 * @returns {boolean}
 */
function resolveFormTabsFromDraft(draft) {
    const form = draft?.builderState?.productBuilderForm;
    return Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
}

function resolveFormTwinForDraftTab(draft, tab, formTabsHint = []) {
    const formTabs = resolveFormTabsFromDraft(draft);
    const tabsForMatch = formTabs.length
        ? formTabs
        : (Array.isArray(formTabsHint) ? formTabsHint : []);
    if (!tabsForMatch.length) {
        return null;
    }
    return findMatchingLiveFurnizorTab(tab, tabsForMatch) || null;
}

/**
 * Existing-looking tab (default / DB id / supplier identity). Used so remount
 * without liveItem does not treat legacy id:null defaults as brand-new.
 *
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function furnizorTabLooksLikeExistingSibling(tab) {
    if (!tab || typeof tab !== 'object') {
        return false;
    }
    if (tab.isDefault) {
        return true;
    }
    if (furnizorTabHasPersistedId(tab)) {
        return true;
    }
    if (tab.furnizorId != null && String(tab.furnizorId).trim() !== '') {
        return true;
    }
    return Boolean(
        String(tab.furnizorCode || '').trim()
        || String(tab.furnizorName || '').trim(),
    );
}

function isDraftFurnizorTabChangedWithoutLive(
    draft,
    formOrPayloadTab,
    payloadTab = null,
    formTabsHint = [],
) {
    const tab = formOrPayloadTab;
    const formTabs = resolveFormTabsFromDraft(draft);
    const tabsForMatch = formTabs.length
        ? formTabs
        : (Array.isArray(formTabsHint) ? formTabsHint : []);
    // Prefer form-shaped twin: baselines store % like the builder form, not
    // payload ratios (0.05 vs "5" would false-positive every tab).
    const formTwin = (tabsForMatch.length
        ? findMatchingLiveFurnizorTab(tab, tabsForMatch)
        : null)
        || (formTabs.length ? null : tab);
    const baseline = resolveOpenBaselineForDraftTab(
        draft,
        formTwin || tab,
        tabsForMatch,
    );
    if (baseline && formTwin) {
        return isFurnizorTabModifiedVsBaseline(formTwin, baseline);
    }
    // No baselines (legacy drafts) and no live product: never invent
    // Actualizare on existing siblings — only brand-new tabs (no identity)
    // stay visible. previousPrice≠payload.price is product-level noise.
    if (furnizorTabLooksLikeExistingSibling(payloadTab || tab)) {
        return false;
    }
    return true;
}

/**
 * @param {Object|null|undefined} draftTab
 * @param {Object|null|undefined} liveTab
 * @param {{
 *   fields?: readonly string[],
 *   includeDiscounts?: boolean,
 *   unmatchedIsChange?: boolean,
 * }} [options]
 *   Default matches every compare field. `unmatchedIsChange` false keeps a
 *   tab with no live twin (new furnizor) out of price-only detection.
 * @returns {boolean}
 */
export function isFurnizorTabModified(draftTab, liveTab, options) {
    const includeDiscounts = !options || options.includeDiscounts !== false;
    const unmatchedIsChange = !options || options.unmatchedIsChange !== false;
    const fields = (options && options.fields) || FURNIZOR_TAB_COMPARE_FIELDS;

    if (!draftTab || !liveTab) {
        return unmatchedIsChange ? Boolean(draftTab) : false;
    }

    // Only compare discounts when the draft/payload actually carries the array.
    // Payload from builder omits `discounts` (legacy fields are enough).
    if (includeDiscounts && Object.prototype.hasOwnProperty.call(draftTab, 'discounts')) {
        const draftDiscounts = Array.isArray(draftTab.discounts) ? draftTab.discounts : [];
        const liveDiscounts = Array.isArray(liveTab.discounts) ? liveTab.discounts : [];
        if (draftDiscounts.length !== liveDiscounts.length) {
            return true;
        }
        for (let i = 0; i < draftDiscounts.length; i += 1) {
            if (
                normalizeComparableNumber(draftDiscounts[i])
                !== normalizeComparableNumber(liveDiscounts[i])
            ) {
                return true;
            }
        }
    }

    return fields.some((field) => {
        // Skip fields omitted from the draft/payload side — missing keys must not
        // count as edits vs live-only form leftovers (false „Actualizare”).
        if (!Object.prototype.hasOwnProperty.call(draftTab, field)) {
            return false;
        }
        // Legacy live rows (flat pricing_detail) often omit discount/markup keys;
        // do not treat payload defaults (0) as edits against missing live fields.
        if (
            !Object.prototype.hasOwnProperty.call(liveTab, field)
            && (liveTab[field] === undefined || liveTab[field] === null)
        ) {
            return false;
        }
        // Formula-auto transport % (green) is derived from the calculator — same as
        // markupActual. Comparing it invents Actualizare on every untouched sibling
        // that still has a formula rate after open→%→save round-trip.
        if (
            field === 'transportCostRate'
            && (
                isFormulaAutoTransportRate(draftTab)
                || isFormulaAutoTransportRate(liveTab)
            )
        ) {
            return false;
        }
        const left = draftTab[field];
        const right = liveTab[field];
        if (
            field === 'usesManufacturerAsFurnizor'
            || field === 'isDefault'
        ) {
            return Boolean(left) !== Boolean(right);
        }
        // Override value only counts when override is active on either side;
        // flag-only / leftover value noise must not invent Actualizare.
        if (field === 'overrideListPriceRonIncVatFinalValue') {
            if (
                !draftTab.overrideListPriceRonIncVatFinal
                && !liveTab.overrideListPriceRonIncVatFinal
            ) {
                return false;
            }
            return normalizeComparableNumber(left) !== normalizeComparableNumber(right);
        }
        if (
            field === 'currency'
            || field === 'furnizorCode'
            || field === 'furnizorName'
            || field === 'furnizorId'
        ) {
            const leftNorm = normalizeComparableScalar(left);
            const rightNorm = normalizeComparableScalar(right);
            // Incomplete identity on one side (legacy flat live vs payload code) is
            // not a user edit — matching already paired these tabs.
            if (
                (field === 'furnizorCode' || field === 'furnizorName' || field === 'furnizorId')
                && (!leftNorm || !rightNorm)
            ) {
                return false;
            }
            return leftNorm !== rightNorm;
        }
        if (FURNIZOR_TAB_RATIO_COMPARE_FIELDS.has(field)) {
            return !areComparableRatesEqual(left, right);
        }
        return normalizeComparableNumber(left) !== normalizeComparableNumber(right);
    });
}

/**
 * Entry/sale inputs on an existing furnizor tab (supplier list, discounts,
 * transport, markup, currency, override). A new tab with no live twin is not
 * a price change — that stays a structure change.
 *
 * @param {Object|null|undefined} draftTab
 * @param {Object|null|undefined} liveTab
 * @returns {boolean}
 */
export function isFurnizorTabPriceModified(draftTab, liveTab) {
    return isFurnizorTabModified(draftTab, liveTab, {
        fields: FURNIZOR_TAB_PRICE_FIELDS,
        includeDiscounts: true,
        unmatchedIsChange: false,
    });
}

/**
 * Furnizor identity (who the supplier is / which one is default).
 * Price inputs alone do not count.
 *
 * @param {Object|null|undefined} draftTab
 * @param {Object|null|undefined} liveTab
 * @returns {boolean}
 */
export function isFurnizorTabStructureModified(draftTab, liveTab) {
    return isFurnizorTabModified(draftTab, liveTab, {
        fields: FURNIZOR_TAB_STRUCTURE_FIELDS,
        includeDiscounts: false,
        unmatchedIsChange: true,
    });
}

/**
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} detail
 * @returns {Object[]}
 */
function resolveDraftFurnizorTabs(form, detail = null) {
    const fromForm = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    if (fromForm.length) {
        return fromForm;
    }
    return Array.isArray(detail?.furnizorPricings) ? detail.furnizorPricings : [];
}

/**
 * Picks furnizor tabs to show as separate "listă în lucru" rows:
 * new tabs, modified tabs, or all tabs on create. Falls back to default
 * when only product-level fields changed.
 *
 * Change detection compares payload furnizorPricings (API/ratio shape) to the
 * live product when available, so form percentage inputs are not compared to
 * stored ratios.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @returns {Object[]}
 */
export function selectDraftFurnizorTabsForDisplay(draft, liveItem = null) {
    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const detail = draft?.payload?.pricing_detail || liveItem?.pricing_detail || null;
    const formTabs = resolveDraftFurnizorTabs(form, detail);

    if (!formTabs.length) {
        return [];
    }
    if (formTabs.length === 1) {
        return formTabs;
    }

    if (draft?.action === 'create') {
        return formTabs;
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const payloadTabs = Array.isArray(draft?.payload?.pricing_detail?.furnizorPricings)
        ? draft.payload.pricing_detail.furnizorPricings
        : [];

    // Prefer payload↔live (same serialization). Fallback to form tabs when
    // payload has no nested furnizor rows yet.
    const compareTabs = payloadTabs.length ? payloadTabs : formTabs;
    const comparingPayload = payloadTabs.length > 0;

    const selectedCompareTabs = compareTabs.filter((tab) => {
        if (isUnpublishedNewFurnizorTab(draft, tab)) {
            return true;
        }
        if (draftHasUnpublishedNewFurnizor(draft)) {
            const formTwin = findMatchingLiveFurnizorTab(tab, formTabs) || tab;
            const payloadTwin = comparingPayload
                ? tab
                : findMatchingLiveFurnizorTab(formTwin, payloadTabs);
            const liveTwin = findMatchingLiveFurnizorTab(payloadTwin || formTwin, liveTabs);
            // No live match: unpublished new tabs already returned true above.
            // Remaining siblings (incl. legacy id:null defaults) must not all
            // become Actualizare after remount when the product is off-page.
            if (!liveTwin) {
                // Unmatched + live on page + no default/id → likely another new
                // supplier (name/code alone is not enough — new tabs have those).
                if (
                    liveTabs.length > 0
                    && !formTwin?.isDefault
                    && !tab?.isDefault
                    && !furnizorTabHasPersistedId(formTwin)
                    && !furnizorTabHasPersistedId(tab)
                ) {
                    return true;
                }
                return isExistingFurnizorChangedWithNewSuppliers(
                    draft,
                    liveItem,
                    null,
                    comparingPayload ? tab : payloadTwin,
                    formTwin,
                );
            }
            return isExistingFurnizorChangedWithNewSuppliers(
                draft,
                liveItem,
                liveTwin,
                comparingPayload ? tab : payloadTwin,
                formTwin,
            );
        }
        const liveTab = findMatchingLiveFurnizorTab(tab, liveTabs);
        if (!liveTab) {
            // Live product present but this tab unmatched → new supplier.
            if (liveTabs.length > 0) {
                if (tab?.isDefault || furnizorTabHasPersistedId(tab)) {
                    return isDraftFurnizorTabChangedWithoutLive(
                        draft,
                        findMatchingLiveFurnizorTab(tab, formTabs) || tab,
                        comparingPayload ? tab : null,
                        formTabs,
                    );
                }
                return true;
            }
            // No live product (paginated remount): compare vs open baselines so
            // untouched siblings are not all selected as Actualizare.
            const formTwin = findMatchingLiveFurnizorTab(tab, formTabs) || tab;
            return isDraftFurnizorTabChangedWithoutLive(
                draft,
                formTwin,
                comparingPayload ? tab : findMatchingLiveFurnizorTab(formTwin, payloadTabs),
                formTabs,
            );
        }
        // Form percentage fields vs live ratios → false positives; only compare payload.
        if (!comparingPayload) {
            return false;
        }
        // Prefer open-session baselines when present (same as display-kind path).
        const formTwin = findMatchingLiveFurnizorTab(tab, formTabs);
        const baseline = resolveOpenBaselineForDraftTab(draft, formTwin || tab, formTabs);
        if (baseline && formTwin) {
            return isFurnizorTabModifiedVsBaseline(formTwin, baseline);
        }
        return isFurnizorTabModified(tab, liveTab);
    });

    if (!selectedCompareTabs.length) {
        const defaultTab = formTabs.find((tab) => tab?.isDefault) || formTabs[0];
        return defaultTab ? [defaultTab] : [];
    }

    const selectedFormTabs = formTabs.filter((formTab) => (
        selectedCompareTabs.some((compareTab) => (
            findMatchingLiveFurnizorTab(formTab, [compareTab]) != null
            || (
                String(formTab?.clientKey || '').trim()
                && String(formTab.clientKey).trim() === String(compareTab?.clientKey || '').trim()
            )
        ))
    ));

    if (selectedFormTabs.length) {
        return selectedFormTabs;
    }

    // Payload tabs without matching form clientKeys — still show form defaults
    // for each selected compare tab by order when possible.
    return selectedCompareTabs.map((compareTab, index) => {
        const matched = findMatchingLiveFurnizorTab(compareTab, formTabs);
        return matched || formTabs[index] || formTabs[0];
    }).filter(Boolean);
}

/**
 * @param {Object|null|undefined} form
 * @param {Object|null|undefined} pricingSettings
 * @param {Object|null|undefined} [builderState]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {{
 *   entryPriceRonExVat: number|null,
 *   entryPriceRonExVatOld: number|null,
 *   markupReference: number|null,
 *   markupActual: number|null,
 *   listPriceRonIncVatReference: number|null,
 *   listPriceRonIncVatFinal: number|null,
 *   priceAdjustmentRatio: number|null,
 * }|null}
 */
export function computeBuilderDraftPricing(
    form,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    builderState = null,
    furnizorTab = null,
) {
    if (!form || typeof form !== 'object') {
        return null;
    }

    const tab = furnizorTab
        || getDefaultFurnizorPricing(form)
        || form.furnizorPricings?.[0]
        || null;
    if (!tab) {
        return null;
    }

    const settings = pricingSettings || DEFAULT_PRICING_SETTINGS;
    const merged = mergeFormWithFurnizorPricing(form, tab);
    const calculatorInput = builderFormToCalculatorInput(merged);
    const computed = calculateProductPricing(calculatorInput, settings);

    const listReference = toNullableNumber(computed.listPriceRonIncVatReference);
    const listFinal = toNullableNumber(computed.listPriceRonIncVatFinal);
    const rawStoredAdjustment = merged.priceAdjustmentRatio;
    const hasStoredAdjustment = rawStoredAdjustment !== null
        && rawStoredAdjustment !== undefined
        && String(rawStoredAdjustment).trim() !== '';
    const storedAdjustment = hasStoredAdjustment
        ? toNullableNumber(calculatorInput.priceAdjustmentRatio)
        : null;
    const computedAdjustment = listReference > 0 && listFinal != null
        ? calculatePriceAdjustmentRatio(listReference, listFinal)
        : toNullableNumber(computed.computedPriceAdjustmentRatio);

    // Prefer frozen open-session (lista veche) baselines. After draft save,
    // supplier old is normalized to new, so the reference snapshot often equals
    // the current price — and last-în-lucru Valoare veche must not be used here.
    const baselineOld = resolveEntryPriceRonExVatOldFromBaselines(
        builderState,
        tab.clientKey,
    );
    const baselineListPrevious = resolveListPricePreviousFromBaselines(
        builderState,
        tab.clientKey,
    );

    return {
        entryPriceRonExVat: toNullableNumber(computed.entryPriceRonExVat),
        // Only frozen open-session baseline here; live/lista-veche fallbacks
        // are applied in resolveDraftTablePricing (reference snapshot often
        // equals current price after draft normalize).
        entryPriceRonExVatOld: baselineOld,
        markupReference: toNullableNumber(calculatorInput.markupReference),
        markupActual: toNullableNumber(computed.markupActual),
        listPriceRonIncVatReference: baselineListPrevious ?? listReference,
        listPriceRonIncVatFinal: listFinal,
        priceAdjustmentRatio: storedAdjustment != null ? storedAdjustment : computedAdjustment,
    };
}

/**
 * Clears old-price columns (UI shows „—” via formatRonPrice).
 *
 * @param {Object} pricing
 * @returns {Object}
 */
export function clearDraftPriorPriceColumns(pricing) {
    return {
        ...pricing,
        supplier_list_price_old: null,
        supplier_list_price_adjustment_ratio: null,
        entry_price_ron_inc_vat_old: null,
        entry_price_adjustment_ratio: null,
        previous_price: null,
        previous_price_ex_vat: null,
        price_adjustment_ratio: null,
        markup_actual_old: null,
        markup_actual_adjustment_ratio: null,
    };
}

/**
 * VAT multiplier used to derive sale price without VAT (inc = ex × vatRate).
 *
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} liveItem
 * @returns {number}
 */
function resolveDraftVatRate(draft, liveItem) {
    const candidates = [
        draft?.builderState?.pricingSettings?.vatRate,
        draft?.payload?.pricing_detail?.pricingSettingsSnapshot?.vatRate,
        liveItem?.pricing_detail?.pricingSettingsSnapshot?.vatRate,
    ];
    for (const candidate of candidates) {
        const rate = toNullableNumber(candidate);
        if (rate != null && rate > 0) {
            return rate;
        }
    }
    return DEFAULT_PRICING_SETTINGS.vatRate;
}

/**
 * Sale price without VAT from the with-VAT amount shown in the same row.
 *
 * @param {number|null|undefined} priceIncVat
 * @param {number} vatRate
 * @returns {number|null}
 */
function salePriceExVat(priceIncVat, vatRate) {
    const price = toNullableNumber(priceIncVat);
    if (price == null) {
        return null;
    }
    const rate = toNullableNumber(vatRate);
    if (rate == null || rate <= 0) {
        return null;
    }
    return roundMoney(price / rate, 2);
}

/**
 * @param {Object} pricing
 * @param {number} vatRate
 * @returns {Object}
 */
function withSalePricesExVat(pricing, vatRate) {
    return {
        ...pricing,
        previous_price_ex_vat: salePriceExVat(pricing.previous_price, vatRate),
        price_ex_vat: salePriceExVat(pricing.price, vatRate),
    };
}

/**
 * Resolves acquisition / sale / list pricing fields for a draft table row.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {Object|null} [furnizorTab]
 * @returns {{
 *   entry_price_ron_inc_vat_old: number|null,
 *   entry_price_ron_inc_vat: number|null,
 *   entry_price_adjustment_ratio: number|null,
 *   markup_reference: number|null,
 *   markup_actual: number|null,
 *   markup_actual_old: number|null,
 *   markup_actual_adjustment_ratio: number|null,
 *   previous_price: number|null,
 *   previous_price_ex_vat: number|null,
 *   price: number|null,
 *   price_ex_vat: number|null,
 *   price_adjustment_ratio: number|null,
 *   supplier_list_price_old: number|null,
 *   supplier_list_price_new: number|null,
 *   supplier_list_price_adjustment_ratio: number|null,
 *   currency: string,
 *   is_default: boolean,
 * }}
 */
export function resolveDraftTablePricing(draft, liveItem = null, furnizorTab = null) {
    // Prefer published list price (lista veche) over any draft-cached previous.
    const previousSalePrice = toNullableNumber(
        liveItem?.price ?? draft?.previousPrice ?? draft?.summary?.previous_price,
    );
    const newSalePrice = toNullableNumber(
        draft?.summary?.price ?? draft?.payload?.price,
    );
    const noPriorListPrice = draftRowHasNoPriorListPrice(draft, liveItem, furnizorTab);
    const vatRate = resolveDraftVatRate(draft, liveItem);

    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const liveDetail = liveItem?.pricing_detail || null;
    const liveTabs = Array.isArray(liveDetail?.furnizorPricings)
        ? liveDetail.furnizorPricings
        : [];
    const liveTab = furnizorTab
        ? findMatchingLiveFurnizorTab(furnizorTab, liveTabs)
        : (liveTabs.find((row) => row?.isDefault) || liveTabs[0] || null);
    const liveEntryPrice = toNullableNumber(
        liveItem?.entry_price_ron_ex_vat
            ?? liveDetail?.entryPriceRonExVat,
    );
    const liveTabSalePrice = toNullableNumber(
        liveTab?.listPriceRonIncVatFinal
            ?? liveTab?.manualListPriceRonIncVatFinal
            ?? liveTab?.overrideListPriceRonIncVatFinalValue,
    );
    const supplierFields = resolveSupplierListDisplayFields(
        furnizorTab,
        form,
        builderState,
        liveItem,
    );
    const builderPricing = computeBuilderDraftPricing(
        form,
        builderState?.pricingSettings,
        builderState,
        furnizorTab,
    );

    if (builderPricing) {
        const useProductLevelLive = !furnizorTab || Boolean(furnizorTab.isDefault);
        let liveComputedEntry = null;
        if (liveTab && form) {
            const settings = builderState?.pricingSettings || DEFAULT_PRICING_SETTINGS;
            const merged = mergeFormWithFurnizorPricing(form, liveTab);
            liveComputedEntry = toNullableNumber(
                calculateProductPricing(
                    builderFormToCalculatorInput(merged),
                    settings,
                ).entryPriceRonExVat,
            );
        }
        // Lista veche: frozen open baselines, then published live tab/item.
        const entryOld = noPriorListPrice
            ? null
            : (
                builderPricing.entryPriceRonExVatOld
                ?? liveComputedEntry
                ?? (useProductLevelLive ? liveEntryPrice : null)
            );
        const listPrevious = noPriorListPrice
            ? null
            : (
                liveTabSalePrice
                ?? builderPricing.listPriceRonIncVatReference
                ?? previousSalePrice
            );
        const listNew = builderPricing.listPriceRonIncVatFinal ?? newSalePrice;

        const previousPrice = noPriorListPrice
            ? null
            : (
                useProductLevelLive
                    ? (previousSalePrice ?? listPrevious)
                    : (listPrevious ?? previousSalePrice)
            );
        const price = listNew ?? newSalePrice;
        // Diferenta (ex-Majorare listă): old vs new sale price only.
        const listMajorare = previousPrice != null && price != null
            ? calculatePriceAdjustmentRatio(previousPrice, price)
            : (noPriorListPrice ? null : builderPricing.priceAdjustmentRatio);

        const entryNew = builderPricing.entryPriceRonExVat;
        const pricingTab = furnizorTab
            || (form ? getDefaultFurnizorPricing(form) : null)
            || form?.furnizorPricings?.[0]
            || null;
        const markupTabKey = String(pricingTab?.clientKey || '').trim();
        const markupNew = builderPricing.markupActual
            ?? toNullableNumber(liveItem?.markup_actual);
        // Lista veche adaos actual: published live tab, then frozen open baseline.
        const markupOld = noPriorListPrice
            ? null
            : (
                toNullableNumber(liveTab?.markupActual)
                ?? resolveMarkupActualOldFromBaselines(builderState, markupTabKey)
                ?? (useProductLevelLive ? toNullableNumber(liveItem?.markup_actual) : null)
            );

        const resolved = withSalePricesExVat({
            entry_price_ron_inc_vat_old: entryOld,
            entry_price_ron_inc_vat: entryNew,
            entry_price_adjustment_ratio: calculatePricingValueChangeRatio(
                entryNew,
                entryOld,
            ),
            markup_reference: builderPricing.markupReference
                ?? toNullableNumber(liveItem?.markup_reference),
            markup_actual: markupNew,
            markup_actual_old: markupOld,
            markup_actual_adjustment_ratio: markupActualChangeRatio(markupNew, markupOld),
            previous_price: previousPrice,
            price,
            price_adjustment_ratio: listMajorare,
            ...supplierFields,
        }, vatRate);
        return noPriorListPrice
            ? clearDraftPriorPriceColumns(resolved)
            : resolved;
    }

    const entryLive = toNullableNumber(
        liveItem?.entry_price_ron_ex_vat
            ?? liveItem?.pricing_detail?.entryPriceRonExVat,
    );
    const previousPrice = noPriorListPrice ? null : previousSalePrice;
    const price = newSalePrice;
    const markupLive = toNullableNumber(liveItem?.markup_actual);
    const markupOld = noPriorListPrice ? null : markupLive;

    const resolved = withSalePricesExVat({
        entry_price_ron_inc_vat_old: noPriorListPrice ? null : entryLive,
        entry_price_ron_inc_vat: entryLive,
        entry_price_adjustment_ratio: calculatePricingValueChangeRatio(
            entryLive,
            noPriorListPrice ? null : entryLive,
        ),
        markup_reference: toNullableNumber(liveItem?.markup_reference),
        markup_actual: markupLive,
        markup_actual_old: markupOld,
        markup_actual_adjustment_ratio: markupActualChangeRatio(markupLive, markupOld),
        previous_price: previousPrice,
        price,
        price_adjustment_ratio:
            previousPrice != null && price != null
                ? calculatePriceAdjustmentRatio(previousPrice, price)
                : null,
        ...supplierFields,
    }, vatRate);
    return noPriorListPrice
        ? clearDraftPriorPriceColumns(resolved)
        : resolved;
}

/**
 * @param {Object|null|undefined} pricing
 * @param {string} [manufacturerName]
 * @returns {string}
 */
export function resolveFurnizorPricingLabel(pricing, manufacturerName = '') {
    if (!pricing || typeof pricing !== 'object') {
        return String(manufacturerName || '').trim();
    }
    const manufacturer = String(manufacturerName || '').trim();
    if (pricing.usesManufacturerAsFurnizor) {
        return manufacturer
            || String(pricing.furnizorName || '').trim()
            || String(pricing.furnizorCode || '').trim()
            || 'Producător';
    }
    return String(pricing.furnizorName || '').trim()
        || String(pricing.furnizorCode || '').trim()
        || manufacturer;
}

/**
 * @param {Object|null|undefined} detail
 * @param {Object|null|undefined} liveItem
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {string}
 */
export function resolveDraftFurnizorLabel(detail, liveItem = null, furnizorTab = null) {
    const manufacturerName = String(
        detail?.manufacturerName
            || liveItem?.pricing_detail?.manufacturerName
            || '',
    ).trim();

    if (furnizorTab) {
        return resolveFurnizorPricingLabel(furnizorTab, manufacturerName);
    }

    if (liveItem?.furnizor) {
        return String(liveItem.furnizor).trim();
    }
    const pricings = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    const defaultPricing = pricings.find((row) => row?.isDefault) || pricings[0];
    if (!defaultPricing) {
        return manufacturerName;
    }
    return resolveFurnizorPricingLabel(defaultPricing, manufacturerName);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimNoteText(value) {
    return String(value ?? '').trim();
}

/**
 * Default furnizor row inside a pricing detail.
 *
 * @param {Object|null|undefined} detail
 * @returns {Object|null}
 */
function resolveDefaultFurnizorPricing(detail) {
    const pricings = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    if (!pricings.length) {
        return null;
    }
    const defaultPricingId = detail?.defaultFurnizorPricingId;
    return pricings.find((row) => Boolean(row?.isDefault))
        || (
            defaultPricingId != null
                ? pricings.find((row) => (
                    row?.id != null
                    && String(row.id) === String(defaultPricingId)
                ))
                : null
        )
        || pricings[0]
        || null;
}

/**
 * Resolves product-level notes for table display (not furnizor notes).
 *
 * @param {Object|null|undefined} itemOrDetail - product row or pricing_detail
 * @returns {string}
 */
export function resolveProductNotes(itemOrDetail) {
    if (!itemOrDetail || typeof itemOrDetail !== 'object') {
        return '';
    }
    const detail = itemOrDetail.pricing_detail || itemOrDetail;
    const candidates = [
        itemOrDetail.productNotes,
        itemOrDetail.product_notes,
        detail?.productNotes,
        detail?.product_notes,
    ];
    for (const candidate of candidates) {
        const text = trimNoteText(candidate);
        if (text) {
            return text;
        }
    }
    return '';
}

/**
 * Resolves furnizor-level notes for table display (not product notes).
 * A furnizor tab wins, including when its note is empty.
 * Otherwise the default furnizor on the product is used.
 *
 * @param {Object|null|undefined} itemOrDetail - product row or pricing_detail
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {string}
 */
export function resolveFurnizorNotes(itemOrDetail, furnizorTab = null) {
    if (furnizorTab && typeof furnizorTab === 'object') {
        return trimNoteText(furnizorTab.notes);
    }
    if (!itemOrDetail || typeof itemOrDetail !== 'object') {
        return '';
    }
    if (Object.prototype.hasOwnProperty.call(itemOrDetail, 'furnizor_notes')) {
        return trimNoteText(itemOrDetail.furnizor_notes);
    }

    const detail = itemOrDetail.pricing_detail
        && typeof itemOrDetail.pricing_detail === 'object'
        ? itemOrDetail.pricing_detail
        : (
            Array.isArray(itemOrDetail.furnizorPricings)
                ? itemOrDetail
                : null
        );
    if (detail) {
        const fromTab = trimNoteText(resolveDefaultFurnizorPricing(detail)?.notes);
        if (fromTab) {
            return fromTab;
        }
        return trimNoteText(detail.notes);
    }

    // Product rows store product notes in `notes`. Only a furnizor object
    // (no product identity) should be read from that field.
    if (itemOrDetail.product_code != null || itemOrDetail.product_name != null) {
        return '';
    }
    return trimNoteText(itemOrDetail.notes);
}

/**
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} [liveItem]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {'new'|'copied'|'new_furnizor'|'update'}
 */
export function resolveDraftDisplayKind(
    draft,
    liveItem = null,
    furnizorTab = null,
) {
    const action = String(
        draft?.action || draft?.summary?.action || 'update',
    ).trim();

    if (action === 'create') {
        const productCode = String(
            draft?.payload?.product_code
                || draft?.summary?.product_code
                || '',
        ).trim();
        const sourceCode = String(
            draft?.sourceProductCode
                || draft?.builderState?.sessionSourceProductCode
                || draft?.payload?.old_product_code
                || '',
        ).trim();
        if (sourceCode && sourceCode !== productCode) {
            return 'copied';
        }
        return 'new';
    }

    // New supplier on an existing product → "Furnizor nou" (same as list compare).
    if (furnizorTab) {
        const tabKey = String(furnizorTab.clientKey || '').trim();
        const unpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
        if (
            tabKey
            && unpublished
            && typeof unpublished === 'object'
            && unpublished[tabKey]
        ) {
            return 'new_furnizor';
        }
        // Need live furnizor rows to decide "new" vs "update". Missing liveItem
        // must not force new_furnizor (would wipe lista veche on ordinary updates).
        if (!liveItem) {
            return 'update';
        }
        const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
        if (liveTabs.length > 0 && !findMatchingLiveFurnizorTab(furnizorTab, liveTabs)) {
            return 'new_furnizor';
        }
    }

    return 'update';
}

/**
 * New product / new furnizor rows have no published „listă veche”.
 * Do not show open-session baselines copied from the source furnizor tab.
 *
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} [liveItem]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {boolean}
 */
export function draftRowHasNoPriorListPrice(draft, liveItem = null, furnizorTab = null) {
    const action = String(
        draft?.action || draft?.summary?.action || 'update',
    ).trim();
    if (action === 'create') {
        return true;
    }
    const tabKey = String(furnizorTab?.clientKey || '').trim();
    const unpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
    if (
        tabKey
        && unpublished
        && typeof unpublished === 'object'
        && unpublished[tabKey]
    ) {
        return true;
    }
    const kind = resolveDraftDisplayKind(draft, liveItem, furnizorTab);
    return kind === 'new' || kind === 'copied' || kind === 'new_furnizor';
}

/**
 * @param {Object|null|undefined} draft
 * @returns {string}
 */
export function resolveDraftSourceProductCode(draft) {
    const productCode = String(
        draft?.payload?.product_code
            || draft?.summary?.product_code
            || '',
    ).trim();
    const sourceCode = String(
        draft?.sourceProductCode
            || draft?.builderState?.sessionSourceProductCode
            || draft?.payload?.old_product_code
            || '',
    ).trim();
    if (sourceCode && sourceCode !== productCode) {
        return sourceCode;
    }
    return '';
}

/**
 * @param {Object|null|undefined} detail
 * @param {Object|null|undefined} liveItem
 * @param {string} detailKey
 * @param {string} liveKey
 * @param {{
 *   payloadHasPricingDetail?: boolean,
 *   draftProductCode?: string,
 *   liveProductCode?: string,
 * }} [options]
 * @param {Object|null|undefined} [furnizorTab]
 * @returns {string}
 */
export function resolveDraftManufacturerDisplayField(
    detail,
    liveItem,
    detailKey,
    liveKey,
    {
        payloadHasPricingDetail = false,
        draftProductCode = '',
        liveProductCode = '',
    } = {},
    furnizorTab = null,
) {
    // Per-furnizor row: never fall back to flat pricing_detail (that mirror is
    // the *default* furnizor and would paint every sibling with the same name).
    if (furnizorTab) {
        if (Object.prototype.hasOwnProperty.call(furnizorTab, detailKey)) {
            return String(furnizorTab[detailKey] ?? '').trim();
        }
        return '';
    }

    if (
        payloadHasPricingDetail
        && detail
        && Object.prototype.hasOwnProperty.call(detail, detailKey)
    ) {
        return String(detail[detailKey] ?? '').trim();
    }

    const sameProduct = !liveProductCode
        || !draftProductCode
        || liveProductCode === draftProductCode;

    if (sameProduct) {
        const liveValue = liveItem?.[liveKey];
        if (liveValue != null && String(liveValue).trim()) {
            return String(liveValue).trim();
        }
    }

    if (detail?.[detailKey] != null && String(detail[detailKey]).trim()) {
        return String(detail[detailKey]).trim();
    }

    return '';
}

/**
 * Builds a draft display row shaped like the main products table (+ draft metadata).
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {Object|null} [furnizorTab]
 * @returns {Object}
 */
export function buildDraftDisplayRow(draft, liveItem = null, furnizorTab = null) {
    let pricing = resolveDraftTablePricing(draft, liveItem, furnizorTab);
    const payload = draft?.payload || {};
    const payloadHasPricingDetail = Boolean(payload.pricing_detail);
    const detail = payload.pricing_detail ?? liveItem?.pricing_detail ?? null;
    const productCode = String(
        draft?.summary?.product_code
            ?? payload.product_code
            ?? liveItem?.product_code
            ?? '',
    ).trim();
    // Prefer payload / builder form over summary — summary can stay stale after
    // a denumire-comercială-only draft save + API reload.
    const productName = String(
        payload.product_name
            ?? draft?.builderState?.productBuilderForm?.productName
            ?? draft?.summary?.product_name
            ?? liveItem?.product_name
            ?? '',
    ).trim();
    const notes = resolveProductNotes({ pricing_detail: detail })
        || resolveProductNotes(liveItem);
    const furnizorNotes = furnizorTab
        ? resolveFurnizorNotes(null, furnizorTab)
        : (
            payloadHasPricingDetail
                ? resolveFurnizorNotes({ pricing_detail: detail })
                : (
                    resolveFurnizorNotes(liveItem)
                    || resolveFurnizorNotes({ pricing_detail: detail })
                )
        );
    const tabKey = String(furnizorTab?.clientKey || furnizorTab?.id || '').trim();
    const rowId = tabKey
        ? `${draft?.id}::${tabKey}`
        : draft?.id;
    const manufacturerFieldOptions = {
        payloadHasPricingDetail,
        draftProductCode: String(payload.product_code || productCode || '').trim(),
        liveProductCode: String(liveItem?.product_code || '').trim(),
    };
    const draftKind = resolveDraftDisplayKind(draft, liveItem, furnizorTab);
    if (
        draftRowHasNoPriorListPrice(draft, liveItem, furnizorTab)
        || draftKind === 'new'
        || draftKind === 'copied'
        || draftKind === 'new_furnizor'
    ) {
        pricing = clearDraftPriorPriceColumns(pricing);
    }

    return {
        ...pricing,
        id: rowId,
        draft,
        furnizorTabKey: tabKey || null,
        action: draft?.action || 'update',
        updateMode: draft?.updateMode || draft?.summary?.update_mode || 'simple',
        serial_number: Boolean(
            detail?.serialNumber ?? liveItem?.serial_number,
        ),
        product_code: productCode,
        product_name: productName,
        status: payload.status ?? liveItem?.status ?? null,
        notes,
        furnizor_notes: furnizorNotes,
        draft_kind: draftKind,
        draft_source_product_code: resolveDraftSourceProductCode(draft),
        manufacturer_product_code: resolveDraftManufacturerDisplayField(
            detail,
            liveItem,
            'manufacturerProductCode',
            'manufacturer_product_code',
            manufacturerFieldOptions,
            furnizorTab,
        ),
        manufacturer_product_name: resolveDraftManufacturerDisplayField(
            detail,
            liveItem,
            'manufacturerProductName',
            'manufacturer_product_name',
            manufacturerFieldOptions,
            furnizorTab,
        ),
        measurement_unit:
            payload.measurement_unit
            ?? liveItem?.measurement_unit
            ?? '',
        furnizor: resolveDraftFurnizorLabel(detail, liveItem, furnizorTab),
        pricing_detail: detail,
        categories: payload.categories || liveItem?.categories || [],
        category_assignments:
            payload.category_assignments
            || liveItem?.category_assignments
            || [],
        systems: detail?.systems || liveItem?.systems || [],
        system1: detail?.system1 ?? liveItem?.system1,
        system2: detail?.system2 ?? liveItem?.system2,
        system3: detail?.system3 ?? liveItem?.system3,
    };
}

/**
 * Stable key for a draft furnizor tab (clientKey preferred, else id).
 *
 * @param {Object|null|undefined} tab
 * @returns {string}
 */
export function resolveDraftFurnizorTabKey(tab) {
    return String(tab?.clientKey || tab?.id || '').trim();
}

/**
 * @param {Object|null|undefined} draft
 * @returns {Record<string, boolean>}
 */
function getUnpublishedNewFurnizorKeySet(draft) {
    const fromUnpublished = draft?.builderState?.unpublishedNewFurnizorKeys;
    const fromPending = draft?.builderState?.newFurnizorKeysPendingFirstSave;
    const merged = {};
    if (fromUnpublished && typeof fromUnpublished === 'object') {
        Object.assign(merged, fromUnpublished);
    }
    if (fromPending && typeof fromPending === 'object') {
        Object.assign(merged, fromPending);
    }
    return merged;
}

/**
 * @param {Object|null|undefined} draft
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function isUnpublishedNewFurnizorTab(draft, tab) {
    const tabKey = resolveDraftFurnizorTabKey(tab);
    const unpublished = getUnpublishedNewFurnizorKeySet(draft);
    return Boolean(tabKey && unpublished[tabKey]);
}

/**
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
function draftHasUnpublishedNewFurnizor(draft) {
    const unpublished = getUnpublishedNewFurnizorKeySet(draft);
    return Object.keys(unpublished).some((key) => Boolean(unpublished[key]));
}

/**
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function furnizorTabHasPersistedId(tab) {
    return tab?.id != null && String(tab.id).trim() !== '';
}

/**
 * Heuristic for „only added new supplier(s)” drafts when live cache / unpublished
 * keys are incomplete: mix of persisted ids + brand-new tabs without id.
 *
 * @param {Object|null|undefined} draft
 * @param {Object[]} formOrPayloadTabs
 * @returns {boolean}
 */
function draftLooksLikeAddFurnizorOnly(draft, formOrPayloadTabs = []) {
    if (draftHasUnpublishedNewFurnizor(draft)) {
        return true;
    }
    if (String(draft?.action || '').trim() === 'create') {
        return false;
    }
    const tabs = Array.isArray(formOrPayloadTabs) ? formOrPayloadTabs : [];
    if (tabs.length < 2) {
        return false;
    }
    const hasPersisted = tabs.some((tab) => furnizorTabHasPersistedId(tab));
    const hasBrandNew = tabs.some((tab) => !furnizorTabHasPersistedId(tab));
    return hasPersisted && hasBrandNew;
}

/**
 * True when supplier/entry old vs new columns differ (ignores sale-price recompute drift
 * and sub-cent rounding noise e.g. 9.99 vs 10).
 *
 * @param {Object} draft
 * @param {Object|null|undefined} liveItem
 * @param {Object} furnizorTab
 * @param {{ includeEntry?: boolean }} [options]
 * @returns {boolean}
 */
function draftExistingFurnizorHasVisiblePricingChange(
    draft,
    liveItem,
    furnizorTab,
    options = {},
) {
    const includeEntry = options.includeEntry !== false;
    // Without a live product (paginated remount), do not invent Actualizare from
    // product-level previousPrice≠payload.price — that fires on the default tab
    // whenever a sibling was added/edited and the builder recomputed list price.
    if (!liveItem) {
        return false;
    }

    const pricing = resolveDraftTablePricing(draft, liveItem, furnizorTab);
    // Same supplier/entry columns the draft table shows (0% → unchanged).
    // Do not use sale previous↔price here: fixtures and recompute drift often
    // differ while supplier/entry stay flat.
    // Entry includes transport — formula-auto rate drift invents Actualizare on
    // untouched siblings, so callers that only need "price edited" pass
    // includeEntry: false.
    const pairs = [
        [pricing.supplier_list_price_old, pricing.supplier_list_price_new],
    ];
    if (includeEntry) {
        pairs.push([
            pricing.entry_price_ron_inc_vat_old,
            pricing.entry_price_ron_inc_vat,
        ]);
    }
    for (const [oldVal, newVal] of pairs) {
        const oldNum = toNullableNumber(oldVal);
        const newNum = toNullableNumber(newVal);
        if (oldNum == null || newNum == null) {
            continue;
        }
        const abs = Math.abs(oldNum - newNum);
        if (abs <= 0.02) {
            continue;
        }
        const rel = abs / Math.max(Math.abs(oldNum), Math.abs(newNum), 1e-9);
        if (rel <= 0.002) {
            continue;
        }
        return true;
    }
    return false;
}

/**
 * Existing furnizor changed meaningfully while draft also adds new suppliers.
 * Uses table-visible supplier/entry old vs new (0% → unchanged), plus a real
 * override *value* change vs live sale — not a bare override flag mismatch.
 *
 * @param {Object} draft
 * @param {Object|null|undefined} liveItem
 * @param {Object|null|undefined} liveTab
 * @param {Object|null|undefined} payloadTab
 * @param {Object} formOrPayloadTab
 * @returns {boolean}
 */
function isExistingFurnizorChangedWithNewSuppliers(
    draft,
    liveItem,
    liveTab,
    payloadTab,
    formOrPayloadTab,
) {
    const displayTab = payloadTab || formOrPayloadTab;
    // Supplier list old≠new only. Entry old≠new drifts (FX / formula) when a
    // sibling furnizor is added and would invent Actualizare with no real edit.
    if (draftExistingFurnizorHasVisiblePricingChange(
        draft,
        liveItem,
        displayTab,
        { includeEntry: false },
    )) {
        return true;
    }

    // Open-session baselines (form %) are the ground truth for „did the user
    // edit this tab?”. Prefer them over payload↔live: save/recompute often
    // rewrites transport/markup slightly and invented Actualizare on Hy Pipe
    // while supplier list stayed at 0%.
    const formTabs = resolveFormTabsFromDraft(draft);
    const formTwin = resolveFormTwinForDraftTab(
        draft,
        formOrPayloadTab || payloadTab || displayTab,
        formTabs,
    );
    const baseline = resolveOpenBaselineForDraftTab(
        draft,
        formTwin || formOrPayloadTab || displayTab,
        formTabs,
    );
    if (baseline && formTwin) {
        return isFurnizorTabModifiedVsBaseline(formTwin, baseline);
    }
    // Only compare payload↔live (same ratio serialization). Never form % vs
    // live ratios — that false-positives every discount/markup field.
    if (liveTab && payloadTab && isFurnizorTabModified(payloadTab, liveTab)) {
        return true;
    }
    // Override value actually moved the sale price vs live (flag-only noise ignored).
    if (liveTab && payloadTab && Boolean(payloadTab.overrideListPriceRonIncVatFinal)) {
        const draftSale = toNullableNumber(payloadTab.overrideListPriceRonIncVatFinalValue);
        const liveSale = toNullableNumber(
            liveTab.listPriceRonIncVatFinal
            ?? liveTab.overrideListPriceRonIncVatFinalValue
            ?? liveItem?.price,
        );
        if (draftSale != null && liveSale != null) {
            const abs = Math.abs(draftSale - liveSale);
            if (abs > 0.05) {
                const rel = abs / Math.max(Math.abs(liveSale), 1e-9);
                if (rel > 0.005) {
                    return true;
                }
            }
        }
    }
    return false;
}

/**
 * Whether a form/payload furnizor tab should appear as a meaningful draft row
 * (new or actually modified). Unchanged existing siblings return false so they
 * can stay collapsed.
 *
 * Rules:
 * - flagged new tab → visible (Furnizor nou)
 * - unflagged tab with no live match → visible (Furnizor nou): e.g. first
 *   supplier filled on a product that had none (default tab never enters
 *   unpublished keys; only Adaugă furnizor does)
 * - existing tab with visible old≠new prices → visible (Actualizare)
 * - existing tab with 0% / identical old&new → collapsed, no chip
 *
 * @param {Object|null|undefined} tab form or payload tab
 * @param {Object[]|null|undefined} liveTabs
 * @param {Object[]|null|undefined} [compareTabs] payload tabs (API shape)
 * @param {Object|null|undefined} [draft]
 * @param {Object[]|null|undefined} [allDraftTabs] form+payload tabs for heuristics
 * @param {Object|null|undefined} [liveItem] full live product (for price columns)
 * @returns {boolean}
 */
export function isDraftFurnizorTabNewOrModified(
    tab,
    liveTabs = [],
    compareTabs = null,
    draft = null,
    allDraftTabs = null,
    liveItem = null,
) {
    if (!tab) {
        return false;
    }
    if (isUnpublishedNewFurnizorTab(draft, tab)) {
        return true;
    }

    const payloadTabs = Array.isArray(compareTabs) ? compareTabs : [];
    const siblingTabs = Array.isArray(allDraftTabs) && allDraftTabs.length
        ? allDraftTabs
        : (payloadTabs.length ? payloadTabs : [tab]);

    const payloadTab = payloadTabs.length
        ? (
            findMatchingLiveFurnizorTab(tab, payloadTabs)
            || (
                (() => {
                    const liveHint = findMatchingLiveFurnizorTab(tab, liveTabs);
                    return liveHint
                        ? findMatchingLiveFurnizorTab(liveHint, payloadTabs)
                        : null;
                })()
            )
        )
        : null;

    const liveTab = findMatchingLiveFurnizorTab(payloadTab || tab, liveTabs)
        || findMatchingLiveFurnizorTab(tab, liveTabs);

    // Adding new supplier(s): existing tabs use core pricing inputs vs live
    // (not recomputed sale price — that drifted and invented Actualizare at 0%).
    if (draftHasUnpublishedNewFurnizor(draft)) {
        // Unpublished new tabs already returned true above. Existing siblings
        // (including legacy defaults with id:null) collapse unless inputs
        // actually changed — bare `return true` invented Actualizare on Hy Pipe
        // after remount when the product was not on the current page.
        if (!liveTab) {
            if (
                liveTabs.length > 0
                && !tab?.isDefault
                && !furnizorTabHasPersistedId(tab)
            ) {
                return true;
            }
            return isExistingFurnizorChangedWithNewSuppliers(
                draft,
                liveItem,
                null,
                payloadTab,
                tab,
            );
        }
        return isExistingFurnizorChangedWithNewSuppliers(
            draft,
            liveItem,
            liveTab,
            payloadTab,
            tab,
        );
    }

    // Add-furnizor without unpublished keys (id heuristic only).
    // Brand-new = !persistedId (new suppliers also have name/code — do not use
    // looksLikeExisting here or Furnizor nou rows disappear).
    if (draftLooksLikeAddFurnizorOnly(draft, siblingTabs)) {
        if (!liveTab) {
            if (tab?.isDefault || furnizorTabHasPersistedId(tab)) {
                return isExistingFurnizorChangedWithNewSuppliers(
                    draft,
                    liveItem,
                    null,
                    payloadTab,
                    tab,
                );
            }
            return true;
        }
        return isExistingFurnizorChangedWithNewSuppliers(
            draft,
            liveItem,
            liveTab,
            payloadTab,
            tab,
        );
    }

    if (!liveTab) {
        // Live product present but unmatched → new supplier (unless default /
        // persisted id — matching can fail on flat vs nested for those).
        if (liveTabs.length > 0) {
            if (tab?.isDefault || furnizorTabHasPersistedId(tab)) {
                return isExistingFurnizorChangedWithNewSuppliers(
                    draft,
                    liveItem,
                    null,
                    payloadTab,
                    tab,
                );
            }
            return true;
        }
        // No live product after remount/page change: use open-session baselines
        // (or soft existing-sibling fallback) instead of inventing Actualizare.
        return isDraftFurnizorTabChangedWithoutLive(
            draft,
            tab,
            payloadTab,
            siblingTabs,
        );
    }
    if (!payloadTab) {
        return false;
    }
    // Supplier list price only — entry old≠new drifts when formula transport
    // (green) recalculates on untouched siblings and would invent Actualizare.
    if (draftExistingFurnizorHasVisiblePricingChange(
        draft,
        liveItem,
        tab,
        { includeEntry: false },
    )) {
        return true;
    }
    // When open-session baselines exist, they decide — soft payload↔live drift
    // (transport/markup recompute) must not invent Actualizare on siblings.
    const formTwin = resolveFormTwinForDraftTab(draft, tab, siblingTabs);
    const baseline = resolveOpenBaselineForDraftTab(
        draft,
        formTwin || tab,
        resolveFormTabsFromDraft(draft),
    );
    if (baseline && formTwin) {
        return isFurnizorTabModifiedVsBaseline(formTwin, baseline);
    }
    if (liveTab && payloadTab && Boolean(payloadTab.overrideListPriceRonIncVatFinal)) {
        const draftSale = toNullableNumber(payloadTab.overrideListPriceRonIncVatFinalValue);
        const liveSale = toNullableNumber(
            liveTab.listPriceRonIncVatFinal
            ?? liveTab.overrideListPriceRonIncVatFinalValue
            ?? liveItem?.price,
        );
        if (draftSale != null && liveSale != null) {
            const abs = Math.abs(draftSale - liveSale);
            if (abs > 0.05) {
                const rel = abs / Math.max(Math.abs(liveSale), 1e-9);
                if (rel > 0.005) {
                    return true;
                }
            }
        }
    }
    return isFurnizorTabModified(payloadTab, liveTab);
}

/**
 * @param {Object[]} rows
 * @param {{ hasCollapsibleUnchanged?: boolean }} [meta]
 * @returns {Object[]}
 */
function annotateDraftMultiFurnizorRows(rows, meta = {}) {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) {
        return list;
    }
    const hasCollapsible = Boolean(meta.hasCollapsibleUnchanged);
    const lastIndex = list.length - 1;
    return list.map((row, index) => {
        const isUnchangedChild = row?.draft_kind === 'unchanged';
        const annotated = {
            ...row,
            has_multiple_furnizori: hasCollapsible,
            is_multi_furnizor_child: hasCollapsible && isUnchangedChild,
            is_default: Boolean(row?.is_default),
        };
        if (!hasCollapsible || list.length < 2) {
            return annotated;
        }
        return {
            ...annotated,
            multi_furnizor_group_pos: index === 0
                ? 'start'
                : (index === lastIndex ? 'end' : 'middle'),
        };
    });
}

/**
 * Expands one draft into one or more table rows — one per furnizor tab that
 * should appear in „listă în lucru”.
 *
 * Meaningful rows (new / copied / new_furnizor / update) are always visible.
 * Unchanged existing furnizor siblings are included only when `expanded` is true
 * (collapsed by default), matching multi-furnizor collapse on the live list.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @param {{ expanded?: boolean }} [options]
 * @returns {Object[]}
 */
export function buildDraftDisplayRows(draft, liveItem = null, options = {}) {
    const expanded = Boolean(options.expanded);
    const builderState = draft?.builderState || null;
    const form = builderState?.productBuilderForm || null;
    const detail = draft?.payload?.pricing_detail || liveItem?.pricing_detail || null;
    const formTabs = resolveDraftFurnizorTabs(form, detail);
    const selectedTabs = selectDraftFurnizorTabsForDisplay(draft, liveItem);

    if (!formTabs.length) {
        return annotateDraftMultiFurnizorRows(
            [buildDraftDisplayRow(draft, liveItem)],
            { hasCollapsibleUnchanged: false },
        );
    }

    if (draft?.action === 'create' || formTabs.length === 1) {
        const tabs = selectedTabs.length ? selectedTabs : formTabs;
        return annotateDraftMultiFurnizorRows(
            tabs.map((tab) => buildDraftDisplayRow(draft, liveItem, tab)),
            { hasCollapsibleUnchanged: false },
        );
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const payloadTabs = Array.isArray(draft?.payload?.pricing_detail?.furnizorPricings)
        ? draft.payload.pricing_detail.furnizorPricings
        : [];
    const siblingTabsForHeuristic = payloadTabs.length ? payloadTabs : formTabs;
    const hasFurnizorLevelChanges = formTabs.some((tab) => (
        isDraftFurnizorTabNewOrModified(
            tab,
            liveTabs,
            payloadTabs,
            draft,
            siblingTabsForHeuristic,
            liveItem,
        )
    ));

    const selectedKeys = new Set(
        selectedTabs.map((tab) => resolveDraftFurnizorTabKey(tab)).filter(Boolean),
    );

    const allRows = formTabs.map((tab) => {
        const row = buildDraftDisplayRow(draft, liveItem, tab);
        const tabKey = resolveDraftFurnizorTabKey(tab);
        const isSelected = tabKey
            ? selectedKeys.has(tabKey)
            : selectedTabs.some((selected) => (
                findMatchingLiveFurnizorTab(tab, [selected]) != null
                || selected === tab
            ));

        if (hasFurnizorLevelChanges) {
            if (!isDraftFurnizorTabNewOrModified(
                tab,
                liveTabs,
                payloadTabs,
                draft,
                siblingTabsForHeuristic,
                liveItem,
            )) {
                return { ...row, draft_kind: 'unchanged' };
            }
            return row;
        }

        // Product-level-only edit: keep the fallback selected tab as update;
        // other existing furnizori are unchanged siblings (collapsible).
        if (!isSelected) {
            return { ...row, draft_kind: 'unchanged' };
        }
        return row;
    });

    const hasCollapsibleUnchanged = allRows.some((row) => row.draft_kind === 'unchanged');
    const meaningfulRows = allRows.filter((row) => row.draft_kind !== 'unchanged');
    const unchangedRows = allRows.filter((row) => row.draft_kind === 'unchanged');
    const visibleRows = (!expanded && hasCollapsibleUnchanged)
        ? meaningfulRows
        : [...meaningfulRows, ...unchangedRows];

    if (!visibleRows.length) {
        return annotateDraftMultiFurnizorRows(
            (selectedTabs.length ? selectedTabs : [formTabs[0]]).map((tab) => (
                buildDraftDisplayRow(draft, liveItem, tab)
            )),
            { hasCollapsibleUnchanged: false },
        );
    }

    return annotateDraftMultiFurnizorRows(visibleRows, { hasCollapsibleUnchanged });
}

/**
 * Whether the draft has multiple furnizor tabs so "produs implicit" can change.
 *
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
export function draftCanChangeDefaultFurnizor(draft) {
    const tabs = draft?.builderState?.productBuilderForm?.furnizorPricings;
    return Array.isArray(tabs) && tabs.length > 1;
}

/**
 * Sets the given furnizor tab as default on a draft and refreshes payload price
 * / pricing_detail from the builder form.
 *
 * @param {Object} draft
 * @param {string} furnizorTabKey
 * @returns {Object|null} Updated draft clone, or null if change is not possible
 */
export function applyDraftDefaultFurnizor(draft, furnizorTabKey) {
    if (!draft || typeof draft !== 'object') {
        return null;
    }
    const key = String(furnizorTabKey || '').trim();
    if (!key) {
        return null;
    }
    const next = cloneDeep(draft);
    const form = next.builderState?.productBuilderForm;
    if (!form || typeof form !== 'object') {
        return null;
    }
    const tabs = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    if (tabs.length < 2) {
        return null;
    }
    const target = tabs.find((row) => String(row?.clientKey || '').trim() === key);
    if (!target) {
        return null;
    }
    if (target.isDefault) {
        return next;
    }

    setDefaultFurnizorPricing(form, key);

    const settings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...(next.builderState.pricingSettings || {}),
    };
    next.builderState.pricingSettings = settings;

    const defaultPricing = getDefaultFurnizorPricing(form);
    const priceSource = calculateProductPricing(
        builderFormToCalculatorInput(
            mergeFormWithFurnizorPricing(form, defaultPricing),
        ),
        settings,
    );
    const pricingDetail = builderFormToPricingDetailPayload(
        form,
        settings,
        priceSource,
        priceSource,
    );
    const nextPrice = toNullableNumber(priceSource.listPriceRonIncVatFinal);

    next.payload = {
        ...(next.payload || {}),
        price: nextPrice,
        pricing_detail: pricingDetail,
        pricing_settings: { ...settings },
    };
    if (next.summary && typeof next.summary === 'object') {
        next.summary = {
            ...next.summary,
            price: nextPrice,
        };
    }
    return next;
}
